/**
 * Companies — the operating units beneath a legal entity. Branches,
 * departments and day-to-day documents all belong to one.
 *
 * Arabic and English (C7): every word from the catalogs.
 */
import { Link } from "react-router-dom";
import { StatusBadge } from "@/components/ui";
import { useApiList } from "@/lib/api/hooks";
import { useTranslation } from "@/lib/i18n";
import { OrgListPage } from "./OrgListPage";
import type { CompanyRow, LegalEntityRow } from "./types";

export function CompaniesPage() {
  const { t } = useTranslation();
  const entities = useApiList<LegalEntityRow>(
    ["org", "legal-entities"],
    "/legal-entities",
  );

  return (
    <OrgListPage<CompanyRow>
      title={t("org.companies.title")}
      subtitle={t("org.companies.subtitle")}
      breadcrumb={[t("org.section"), t("org.companies.title")]}
      path="/companies"
      queryKey="companies"
      createLabel={t("org.companies.new")}
      emptyTitle={t("org.companies.emptyTitle")}
      emptyMessage={t("org.companies.emptyMessage")}
      parent={{
        label: t("fields.legalEntity"),
        field: "legalEntityId",
        options: (entities.data?.items ?? []).map((e) => ({
          id: e.id,
          label: e.name,
        })),
        isLoading: entities.isLoading,
        missingMessage: t("org.companies.missingParent"),
      }}
      columns={[
        {
          key: "name",
          label: t("fields.name"),
          render: (r) => (
            <Link to={`/app/organization/companies/${r.id}`}>{r.name}</Link>
          ),
        },
        {
          key: "code",
          label: t("fields.code"),
          render: (r) => <span className="atlas-mono">{r.code}</span>,
        },
        {
          key: "legal_entity_id",
          label: t("fields.legalEntity"),
          render: (r) =>
            entities.data?.items.find((e) => e.id === r.legal_entity_id)
              ?.name ?? "—",
        },
        {
          key: "status",
          label: t("fields.status"),
          render: (r) => <StatusBadge status={r.status} type="Company" />,
        },
      ]}
    />
  );
}
